<template>
  <b-container ref="container" fluid class="position-relative bg-white shadow px-3 pb-5 px-6-lg pb-2-lg w-lg-70">
    <div class="full-mask" v-if="mfsAdmin && !startedByAdmin && !adminEditing"></div>
    <div class="py-3 above-full-mask">
      <h2 >Involvement Details</h2>
      <router-link tag="a" :to="`/notification/${notification.id}`">
        Notification #{{notification.fonNumber}}
      </router-link>
    </div>
    <b-form class="pb-lg-0 pb-md-8 pb-0">
      <ValidationObserver ref="observer" slim>
        <b-card>
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">
              Operator/Harvester Information
            </h5>
          </b-form-row>
          <ValidationProvider
            name="Operator Change"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label id="opChangeLabel" for="opChange" class="col-lg-8 col-12 col-form-label">
                <span class="req-ind">*</span>Was
                <strong>{{contactFullName(notification.operatorContact)}}</strong> the
                Operator/Harvester for this harvest?
              </label>
              <b-form-radio-group
                id="opChange"
                name="opChange"
                v-model="model.operatorChanged"
                aria-labelledby="opChangeLabel"
                :aria-describedby="$errorsIdString(errors, 'opChange')"
              >
                <b-form-radio class="noselect" :value="false">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="true">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="opChange"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <template v-if="model.operatorChanged">
            <ValidationProvider
              name="Operator/Harvester"
              rules="required|min:3"
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group label-cols="12" lg="6" label-for="operatorName">
                <template v-slot:label>
                  <span class="req-ind">*</span>Operator/Harvester Name
                </template>
                <b-form-input
                  id="operatorName"
                  name="operatorName"
                  v-model="model.operatorName"
                  placeholder="Enter Operator/Harvester Name"
                  maxlength="75"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'operatorName')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="operatorName" />
              </b-form-group>
            </ValidationProvider>
            <AddressForm
              :isAdminEdit="userCanReport"
              :address="model.operatorAddress"
              addressType="Operator/Harvester"
              addressSelector="operator"
              smallTitle
            />
          </template>
        </b-card>
        <b-card>
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">
              Management Information
            </h5>
          </b-form-row>
          <ValidationProvider
            name="Management Plan"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label id="managementPlanLabel" for="managementPlan" class="col-lg-8 col-12 col-form-label">
                <span class="req-ind">*</span>Was there a Written Management Plan for this
                property?
                <FontAwesomeIcon
                  focusable="true"
                  tabindex="0"
                  size="lg"
                  class="mx-2 text-primary align-self-center"
                  icon="info-circle"
                  id="management-plan-popover"
                />
                <b-popover
                  target="management-plan-popover"
                  :triggers="['hover', 'focus']"
                  placement="right"
                >
                  If you have an up-to-date forest management plan written or approved by a Maine
                  Licensed Forester for your property check "Yes", otherwise, check "No."
                </b-popover>
              </label>
              <b-form-radio-group
                id="managementPlan"
                name="managementPlan"
                v-model="model.managementPlan"
                aria-labelledby="managementPlanLabel"
                :aria-describedby="$errorsIdString(errors, 'managementPlan')"
              >
                <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="managementPlan"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <ValidationProvider
            v-if="notification.licensedForesterContact"
            name="Licensed Forester Change"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label
                id="licensedForesterChangedLabel"
                for="licensedForesterChanged"
                class="col-lg-8 col-12 col-form-label"
              >
                <span class="req-ind">*</span>Did
                <strong>{{contactFullName(notification.licensedForesterContact)}}</strong> supervise
                the activity with an active Maine Forester license?
                <FontAwesomeIcon
                  focusable="true"
                  tabindex="0"
                  size="lg"
                  class="mx-2 text-primary align-self-center"
                  icon="info-circle"
                  id="lf-change-popover"
                />
                <b-popover
                  target="lf-change-popover"
                  :triggers="['hover', 'focus']"
                  placement="right"
                >
                  If this Maine licensed professional forester was consulted during the harvest,
                  check "Yes", otherwise, check "No."
                </b-popover>
              </label>
              <b-form-radio-group
                id="licensedForesterChanged"
                name="licensedForesterChanged"
                v-model="model.licensedForesterChanged"
                @change="licensedForesterChangedChanged"
                aria-labelledby="licensedForesterChangedLabel"
                :aria-describedby="$errorsIdString(errors, 'licensedForesterChanged')"
              >
                <b-form-radio class="noselect" :value="false">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="true">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="licensedForesterChanged"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <ValidationProvider
            v-if="model.licensedForesterChanged"
            name="Licensed Forester"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label
                id="anotherLicensedForesterLabel"
                for="anotherLicensedForester"
                class="col-lg-8 col-12 col-form-label"
              >
                <span class="req-ind">*</span>Did another Maine Licensed Forester supervise
                this harvest?
              </label>
              <b-form-radio-group
                id="anotherLicensedForester"
                name="anotherLicensedForester"
                v-model="model.anotherLicensedForester"
                :aria-describedby="$errorsIdString(errors, 'anotherLicensedForester')"
                aria-labelledby="anotherLicensedForesterLabel"
              >
                <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="anotherLicensedForester"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <ValidationProvider
            v-if="!notification.licensedForesterContact"
            name="Licensed Forester Added"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label
                id="licensedForesterAddedLabel"
                for="licensedForesterAdded"
                class="col-lg-8 col-12 col-form-label"
              >
                <span class="req-ind">*</span>Did a currently active Maine Licensed forester supervise the activity?
                <FontAwesomeIcon
                  focusable="true"
                  tabindex="0"
                  size="lg"
                  class="mx-2 text-primary align-self-center"
                  icon="info-circle"
                  id="lf-added-popover"
                />
                <b-popover
                  target="lf-added-popover"
                  :triggers="['hover', 'focus']"
                  placement="right"
                >
                  If a Maine licensed professional forester was consulted during the harvest, check
                  "Yes", otherwise, check "No."
                </b-popover>
              </label>
              <b-form-radio-group
                id="licensedForesterAdded"
                name="licensedForesterAdded"
                v-model="model.licensedForesterAdded"
                aria-labelledby="licensedForesterAddedLabel"
                :aria-describedby="$errorsIdString(errors, 'licensedForesterAdded')"
              >
                <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="licensedForesterAdded"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <template v-if="model.licensedForesterAdded || model.anotherLicensedForester">
            <ValidationProvider
              name="Licensed Forester Name"
              rules="required|min:3"
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group label-cols="12" label-cols-lg="6" label-for="licensedForesterName">
                <template v-slot:label>
                  <span class="req-ind">*</span>Licensed Forester Name
                </template>
                <b-form-input
                  id="licensedForesterName"
                  name="licensedForesterName"
                  v-model="model.licensedForesterName"
                  placeholder="Enter Licensed Forester Name"
                  maxlength="75"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'licensedForesterName')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="licensedForesterName" />
              </b-form-group>
            </ValidationProvider>
            <ValidationProvider
              name="License Number"
              rules="required"
              v-slot="{ errors, classes }"
              slim
            >
              <b-form-group label-cols="12" label-cols-lg="6" label-for="licenseNumber">
                <template v-slot:label>
                  <span class="req-ind">*</span>License Number -
                  <a :href="links.licensedForesterSearch" target="_blank" rel="noopener noreferrer">
                    License Status Search
                  </a>
                </template>
                <b-form-input
                  id="licenseNumber"
                  name="licenseNumber"
                  v-model="model.licenseNumber"
                  placeholder="Enter License Number"
                  maxlength="75"
                  :class="classes"
                  :aria-describedby="$errorsIdString(errors, 'licenseNumber')"
                />
                <InputErrors :errors="errors" errorsIdPrefix="licenseNumber" />
              </b-form-group>
            </ValidationProvider>
          </template>
        </b-card>
        <b-card>
          <b-form-row>
            <h5 class="col-12 text-primary font-weight-bold pb-2">
              Export Information
            </h5>
          </b-form-row>
          <ValidationProvider
            name="Exported"
            rules="required"
            v-slot="{ errors }"
            slim
          >
            <div role="group" class="form-row form-group justify-content-between">
              <label id="exportedlabel" for="exported" class="col-lg-8 col-12 col-form-label">
                <span class="req-ind">*</span>Were any forest products exported outside of
                Maine or sold to a broker for export from this harvest?
                <FontAwesomeIcon
                  focusable="true"
                  tabindex="0"
                  size="lg"
                  class="mx-2 text-primary align-self-center"
                  icon="info-circle"
                  id="exported-added-popover"
                />
                <b-popover
                  target="exported-added-popover"
                  :triggers="['hover', 'focus']"
                  placement="right"
                >
                  To the best of your knowledge, were any of the forest products from this harvest
                  exported out of Maine or sold to a broker for export. If so, check "Yes."
                </b-popover>
              </label>
              <b-form-radio-group
                id="exported"
                name="exported"
                v-model="model.exported"
                @change="exportedChanged"
                :aria-describedby="$errorsIdString(errors, 'exported')"
                aria-labelledby="exportedlabel"
              >
                <b-form-radio class="noselect" :value="true">Yes</b-form-radio>
                <b-form-radio class="noselect" :value="false">No</b-form-radio>
              </b-form-radio-group>
              <InputErrors
                :errors="errors"
                errorsIdPrefix="exported"
                class="d-block invalid-feedback text-right"
              />
            </div>
          </ValidationProvider>
          <template v-if="model.exported">
            <div v-for="(exporter, index) in model.exporters" :key="index">
              <hr class="border-dark">
              <b-form-row class="d-flex justify-content-between px-1 mb-2">
                <h5 class="text-primary font-weight-bold">
                  Exporter/Broker {{index + 1}}
                </h5>
                <b-button
                  v-if="model.exporters.length > 1"
                  variant="danger"
                  @click="removeExporter(index)"
                  aria-label="Remove Exporter/Broker"
                >
                  <FontAwesomeIcon class="align-self-center" icon="trash-alt" />
                </b-button>
              </b-form-row>
              <ValidationProvider
                :name="`Exporter/Broker ${index + 1} Name`"
                rules="required|min:3"
                v-slot="{ errors, classes }"
                slim
              >
                <b-form-group label-cols="12" label-cols-lg="6" :label-for="`exporterName-${index + 1}`">
                  <template v-slot:label>
                    <span class="req-ind">*</span>Name
                  </template>
                  <b-form-input
                    :id="`exporterName-${index + 1}`"
                    :name="`exporterName-${index + 1}`"
                    v-model="exporter.name"
                    placeholder="Enter Exporter/Broker Name"
                    maxlength="75"
                    :class="classes"
                    :aria-describedby="$errorsIdString(errors, `exporterName-${index + 1}`)"
                  />
                  <InputErrors :errors="errors" :errorsIdPrefix="`exporterName-${index + 1}`" />
                </b-form-group>
              </ValidationProvider>
              <AddressForm
                :isAdminEdit="userCanReport"
                :address="exporter.address"
                addressType="Exporter"
                :addressSelector="`exporter-${index + 1}`"
                smallTitle
              />
            </div>
            <hr class="border-dark">
            <a class="form-action-text text-secondary"
              @click="addExporter"
            >
              + Add Another Exporter/Broker
            </a>
          </template>
        </b-card>
      </ValidationObserver>
    </b-form>
    <b-card v-if="adminEditing" class="mt-3">
      <b-form-group label="Editor's Comment" label-for="editorCommentInvolvement">
        <b-form-textarea
          id="editorCommentInvolvement"
          v-model="editorComment"
          placeholder="Add a comment about changes made to Involvement..."
          rows="3"
          max-rows="6"
          maxlength="255"
        />
      </b-form-group>
    </b-card>
  </b-container>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import AddressForm from '@/components/AddressForm.vue';
import FormSubmit from '@/mixins/FormSubmit';
import Utilities from '@/mixins/Utilities';
import { initialAddress } from '@/helpers';

export default {
  name: 'InvolvementDetailsForm',
  components: {
    AddressForm,
  },
  mixins: [FormSubmit, Utilities],
  props: ['landownerReport'],
  data() {
    let model = {
      operatorChanged: null,
      operatorName: null,
      operatorAddress: initialAddress(),
      managementPlan: null,
      licensedForesterChanged: null,
      anotherLicensedForester: null,
      licensedForesterName: null,
      licenseNumber: null,
      licensedForesterAdded: null,
      exported: null,
      exporters: [],
    };
    model = this.mergeTargetProps(model, this.landownerReport);
    return {
      model,
    };
  },
  mounted() {
    if (this.mfsAdmin && !this.startedByAdmin && !this.adminEditing) {
      if (this.disableTabbing) {
        setTimeout(() => {
          this.disableTabbing(this.$refs.container);
        }, 1500);
      }
    }
  },
  watch: {
    landownerReport() {
      this.model = this.mergeTargetProps(this.model, this.landownerReport);
    },
  },
  computed: {
    ...mapState('Configuration', {
      links: state => state.links,
    }),
    ...mapState('Notification', {
      notification: state => state.notification,
    }),
    ...mapState('User', {
      user: state => state.user,
    }),
    ...mapGetters('User', ['mfsUser', 'mfsAdmin']),
    ...mapGetters('LandownerReport', ['adminEditing']),
    editorComment: {
      get() {
        return this.$store.state.LandownerReport.editorComments.involvement;
      },
      set(value) {
        this.$store.dispatch('LandownerReport/setEditorComment', { section: 'involvement', comment: value });
      },
    },
    userCanReport() {
      return this.userIsLandowner || this.userIsDesignatedAgent || this.userIsMfsAdmin;
    },
    userIsLandowner() {
      if (this.notification.landownerUser) {
        return this.notification.landownerUser.id === this.user.id;
      }
      return false;
    },
    userIsDesignatedAgent() {
      if (this.notification.designatedAgentUser) {
        return this.notification.designatedAgentUser.id === this.user.id;
      }
      return false;
    },
    userIsMfsAdmin() {
      if (this.mfsAdmin) {
        return true;
      }
      return false;
    },
    startedByAdmin() {
      return this.landownerReport.statusId === 3;
    },
  },
  methods: {
    exportedChanged(value) {
      if (value && !this.model.exporters.length) this.addExporter();
    },
    licensedForesterChangedChanged(value) {
      if (!value) this.model.anotherLicensedForester = null;
    },
    addExporter() {
      this.model.exporters.push({ name: null, address: initialAddress() });
    },
    removeExporter(index) {
      this.model.exporters.splice(index, 1);
    },
    contactFullName(contact) {
      return contact ? `${contact.firstName} ${contact.lastName}` : '';
    },
    async submitWrapper() {
      const submitDto = { ...this.model };
      if (!this.model.operatorChanged) {
        submitDto.operatorAddress = null;
      }
      if (!this.model.exported) {
        submitDto.exporters = [];
      }
      const action = this.adminEditing
        ? 'LandownerReport/adminUpdateInvolvement'
        : 'LandownerReport/updateInvolvement';
      return this.submit(
        this.$refs.observer,
        action,
        submitDto,
      );
    },
  },
};
</script>
